import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { db } from '../instant';
import { DEFAULT_ROUTE, BACKEND_BASE_URL } from '../constants';

interface SignupCodeData {
    code: string;
    permissions: Record<string, boolean>;
    eventIds: string | string[];
    email?: string;
    name?: string;
}

function SignupScreen() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [signupCodeData, setSignupCodeData] = useState<SignupCodeData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [step, setStep] = useState<'form' | 'code' | 'setup'>('form');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [magicCode, setMagicCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSetupRun, setHasSetupRun] = useState(false);
    const { isLoading: authLoading, user: authUser } = db.useAuth();

    const codeFromUrl = searchParams.get('code');

    // Fetch signup code info from backend (no client DB access to signupCodes)
    useEffect(() => {
        if (!codeFromUrl || !codeFromUrl.trim()) {
            setError('Invalid signup link');
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError('');

        fetch(`${BACKEND_BASE_URL}/signup-code-info?code=${encodeURIComponent(codeFromUrl.trim())}`)
            .then(res => res.json())
            .then(
                (data: {
                    valid?: boolean;
                    error?: string;
                    code?: string;
                    permissions?: Record<string, boolean>;
                    eventIds?: string | string[];
                    email?: string;
                    name?: string;
                }) => {
                    if (cancelled) return;
                    if (!data.valid || data.error) {
                        setError(data.error || 'Invalid signup link');
                        setSignupCodeData(null);
                        setLoading(false);
                        return;
                    }
                    if (data.code == null || data.code === '') {
                        setError('Invalid signup link');
                        setSignupCodeData(null);
                        setLoading(false);
                        return;
                    }
                    const signupCodeData: SignupCodeData = {
                        code: data.code,
                        permissions: data.permissions ?? {},
                        eventIds: data.eventIds ?? [],
                        email: data.email,
                        name: data.name,
                    };
                    setSignupCodeData(signupCodeData);
                    if (data.email) setEmail(data.email);
                    if (data.name) setName(data.name);
                    setLoading(false);
                }
            )
            .catch(err => {
                if (!cancelled) {
                    setError('Error loading signup link: ' + (err instanceof Error ? err.message : String(err)));
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [codeFromUrl]);

    // Handle post-authentication setup
    useEffect(() => {
        if (step !== 'setup' || authLoading || !authUser || !signupCodeData || hasSetupRun) {
            return;
        }

        const setupUserPermissions = async () => {
            try {
                // Mark setup as started to prevent double execution
                setHasSetupRun(true);

                // Get auth token
                const token = authUser.refresh_token;
                if (!token) {
                    throw new Error('Authentication token not available');
                }

                // Call backend to claim signup code and set up permissions securely
                const response = await fetch(`${BACKEND_BASE_URL}/claim-code`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        token: token,
                    },
                    body: JSON.stringify({
                        code: signupCodeData.code,
                        name: name || undefined,
                    }),
                });

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(result.error || 'Failed to set up account');
                }

                // Redirect to default route
                navigate(DEFAULT_ROUTE, { replace: true });
            } catch (err) {
                setError('Error setting up account: ' + (err instanceof Error ? err.message : String(err)));
                setIsSubmitting(false);
                setHasSetupRun(false); // Reset flag on error to allow retry
            }
        };

        setupUserPermissions();
    }, [step, authLoading, authUser, signupCodeData, name, navigate, hasSetupRun]);

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (!email) {
                setError('Email is required');
                setIsSubmitting(false);
                return;
            }

            // Send magic code
            await db.auth.sendMagicCode({ email });
            setStep('code');
        } catch (err) {
            setError('Error sending magic code: ' + (err instanceof Error ? err.message : String(err)));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCodeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (!magicCode) {
                setError('Code is required');
                setIsSubmitting(false);
                return;
            }

            if (!signupCodeData) {
                setError('Signup code data missing');
                setIsSubmitting(false);
                return;
            }

            // Sign in with magic code
            await db.auth.signInWithMagicCode({ email, code: magicCode });

            // Move to setup step - useEffect will handle the rest
            setStep('setup');
        } catch (err) {
            setError('Error verifying code: ' + (err instanceof Error ? err.message : String(err)));
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <div style={authStyles.container}>Loading...</div>;
    }

    if (error && !signupCodeData) {
        return (
            <div style={authStyles.container}>
                <div style={authStyles.form}>
                    <div style={{ color: '#d32f2f', padding: '10px', textAlign: 'center' }}>{error}</div>
                </div>
            </div>
        );
    }

    if (step === 'setup') {
        return (
            <div style={authStyles.container}>
                <div style={authStyles.form}>
                    <h2 style={{ color: '#333', marginBottom: '20px', textAlign: 'center' }}>Setting up your account...</h2>
                    {error && <div style={{ color: '#d32f2f', padding: '10px', textAlign: 'center' }}>{error}</div>}
                </div>
            </div>
        );
    }

    return (
        <div style={authStyles.container}>
            {step === 'form' ? (
                <form onSubmit={handleFormSubmit} style={authStyles.form}>
                    <h2 style={{ color: '#333', marginBottom: '20px', textAlign: 'center', fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>
                        Let&apos;s create your account!
                    </h2>

                    {error && <div style={{ color: '#d32f2f', padding: '10px', marginBottom: '15px', textAlign: 'center' }}>{error}</div>}

                    {/* Email Field */}
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {signupCodeData?.email ? (
                            <div
                                style={{
                                    ...authStyles.input,
                                    backgroundColor: '#f5f5f5',
                                    color: '#666',
                                    cursor: 'not-allowed',
                                }}
                            >
                                {signupCodeData.email}
                            </div>
                        ) : (
                            <input
                                style={authStyles.input}
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                            />
                        )}
                    </div>

                    {/* Name Field */}
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <input
                            style={authStyles.input}
                            placeholder="Enter your name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            disabled={!!signupCodeData?.name}
                            required={!signupCodeData?.name}
                        />
                    </div>

                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <button type="submit" style={authStyles.button} disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Code'}
                        </button>
                    </div>
                </form>
            ) : (
                <form onSubmit={handleCodeSubmit} style={authStyles.form}>
                    <h2
                        style={{
                            color: '#333',
                            marginBottom: '20px',
                            textAlign: 'center',
                            fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
                            lineHeight: '1.4',
                        }}
                    >
                        Okay, we sent you an email! What was the code?
                    </h2>

                    {error && <div style={{ color: '#d32f2f', padding: '10px', marginBottom: '15px', textAlign: 'center' }}>{error}</div>}

                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <input
                            style={authStyles.input}
                            type="text"
                            placeholder="123456..."
                            value={magicCode}
                            onChange={e => setMagicCode(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <button type="submit" style={authStyles.button} disabled={isSubmitting}>
                            {isSubmitting ? 'Verifying...' : 'Verify'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

const authStyles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif',
        width: '100%',
        maxWidth: '400px',
    },
    input: {
        padding: '12px',
        marginBottom: '15px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        width: '100%',
        maxWidth: '300px',
        fontSize: '16px',
        boxSizing: 'border-box',
    },
    button: {
        padding: '12px 24px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '16px',
        width: '100%',
        maxWidth: '300px',
    },
};

export default SignupScreen;
